import React, { FC } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { RootState } from '../../../../../../redux/root';
import { ServiceTypeItemType } from '../../../../types';

type Props = {
  values: any;
  onEditStep: (step: number) => void;
};

const join = (...parts: (string | undefined | null | false)[]) =>
  parts.filter(Boolean).join(', ');

const ReviewStep: FC<Props> = ({ values, onEditStep }) => {
  const vocabularies = useSelector((state: RootState) => state.general.vocabulary);
  const filesToUpload = useSelector((s: RootState) => s.documents.filesToUpload);
  const { franchiseUsers, selectedFranchise } = useSelector((s: RootState) => s.projectsSlice);

  const lookupVocabulary = (id: any) => {
    if (!id && id !== 0) return '';
    const found = vocabularies.find(v => String(v.id) === String(id));
    return found ? found.title : '';
  };

  const services = Array.isArray(values.serviceType)
    ? (values.serviceType as ServiceTypeItemType[]).filter(s => s.checked).map(s => s.title)
    : [];

  const applicantType = Array.isArray(values.applicantType) && values.applicantType[0]
    ? values.applicantType[0].title
    : '';

  const franchiseName = Array.isArray(values.franchise) && values.franchise[0]
    ? values.franchise[0].title || values.franchise[0].name
    : '';

  let managerName = '';
  if (values.manager && selectedFranchise.id) {
    const franchaseUsersList = franchiseUsers.find(u => Number(u.id) === selectedFranchise.id);
    if (franchaseUsersList) {
      const m = franchaseUsersList.value.find((u: any) => String(u.id) === String(values.manager));
      if (m) managerName = `${m.firstName || ''} ${m.lastName || ''}`.trim();
    }
  }

  const cityLine = join(values.city, values.state, values.zipCode);

  const Card: FC<{ title: string; step: number; children: React.ReactNode }> = ({
    title,
    step,
    children,
  }) => (
    <Section>
      <SectionHead>
        <SectionTitle>{title}</SectionTitle>
        <EditButton type="button" onClick={() => onEditStep(step)} aria-label={`Edit ${title}`}>
          <EditOutlinedIcon style={{ fontSize: 16 }} />
          Edit
        </EditButton>
      </SectionHead>
      <SectionBody>{children}</SectionBody>
    </Section>
  );

  const Field: FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <FieldItem>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>{value || value === 0 ? value : <Empty>Not provided</Empty>}</FieldValue>
    </FieldItem>
  );

  return (
    <ReviewWrapper>
      <ReviewIntro>
        <ReviewTitle>Review your request</ReviewTitle>
        <ReviewHint>
          Take a moment to verify the details below.
          Tap <strong>Edit</strong> on any section to make changes,
          then submit when you&apos;re ready.
        </ReviewHint>
      </ReviewIntro>

      <Card title="Project Location" step={1}>
        <FieldGrid>
          <Field label="Address" value={values.address} />
          <Field label="Street" value={values.street} />
          <Field label="City / State / Zip" value={cityLine} />
        </FieldGrid>
      </Card>

      <Card title="Project Details" step={2}>
        <FieldGrid>
          <Field label="Project Name" value={values.projectName} />
        </FieldGrid>
        <Field label="Description" value={values.description && <Multiline>{values.description}</Multiline>} />
      </Card>

      <Card title="Services Requested" step={3}>
        {services.length > 0 ? (
          <ChipList>
            {services.map(s => (
              <Chip key={s}>{s}</Chip>
            ))}
          </ChipList>
        ) : (
          <Empty>No services selected</Empty>
        )}
      </Card>

      <Card title="Applicant & Scope" step={4}>
        <FieldGrid>
          <Field label="Applicant Type" value={applicantType} />
          <Field label="Type of Work" value={lookupVocabulary(values.work)} />
          <Field label="Type of Building" value={lookupVocabulary(values.building)} />
        </FieldGrid>
      </Card>

      <Card title="Office" step={5}>
        <FieldGrid>
          <Field label="Processing Office" value={franchiseName} />
          <Field label="Office Contact" value={managerName} />
        </FieldGrid>
      </Card>

      <Card title="Files" step={6}>
        <FieldItem>
          <FieldLabel>Supporting Documents</FieldLabel>
          {filesToUpload.length > 0 ? (
            <FileList>
              {filesToUpload.map((file: any) => (
                <FileChip key={file.path || file.name}>
                  <InsertDriveFileOutlinedIcon style={{ fontSize: 16 }} />
                  <FileName>{file.path || file.name}</FileName>
                </FileChip>
              ))}
            </FileList>
          ) : (
            <Empty>No files attached</Empty>
          )}
        </FieldItem>
      </Card>
    </ReviewWrapper>
  );
};

export default ReviewStep;

const ReviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const ReviewIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 18px;
  background: var(--primary-50);
  border: 1px solid var(--primary-100, #cfe6f3);
  border-radius: 10px;
`;

const ReviewTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--primary-700, #0b5a87);
`;

const ReviewHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.45;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  box-sizing: border-box;

  @media only screen and (max-width: 540px) {
    padding: 14px 16px;
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed var(--border);
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700, #0b5a87);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 24px;
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FieldLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const FieldValue = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
  word-break: break-word;
`;

const Multiline = styled.div`
  white-space: pre-wrap;
  line-height: 1.5;
`;

const Empty = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-400);
  font-style: italic;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  background: var(--primary-50);
  color: var(--primary-700, #0b5a87);
  border: 1px solid var(--primary-100, #cfe6f3);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 6px 10px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--neutral-700);
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 0;
`;

const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
`;
